import {cdnImage} from '@/util/cdnImage';
import {colors} from '@/styles/tokens';
import AggregateScript from '@/components/Common/Aggregate-script';
import {MUIContainer} from '@/components/MUI-Components';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import {GENERATIVE_AI_INTEGRATION_META_DATA} from '@/util/metaData/ai';
import {CDN_URL, SITE_URL} from '@/constants/urls';
import {meetingLink} from '@/constants';

import HeroVideoBanner from '@/components/Ui-components/CommonSections/HeroVideoBanner';
import BannerBottom from '@/components/Ui-components/banner-bottom';
import {aiAwardsList} from '@/util/contents/ai-copilot';
import {aiPortfolio} from '@/util/contents/latest-portfolio-data';
import {aiCarsoulData} from '@/util/contents/ai-development-company';
import {
    genAiArchitectureData,
    genAiArchitectureImage,
    genAiBannerBottom,
    genAiBusinessValueData,
    genAiChallengesData,
    genAiComplianceFrameworks,
    genAiCostBreakdownData,
    genAiCTA1Data,
    genAiCTA2Data,
    genAiDevProcessData,
    genAiEnterpriseControlsData,
    genAiFAQData,
    genAiIndustrySolutionsData,
    genAiKeyServicesData,
    genAiOptimizationData,
    genAiReadinessData,
    genAiReadyCtaImage,
    genAiScaleCtaImage,
    genAiServicesImage,
    genAiTechEcosystemData,
    genAiWhatSetsBestData
} from '@/util/contents/generative-ai-integration/generative-ai-integration-data';

const FeatureGrid = dynamic(() => import('@/components/case-study/sections/FeatureGrid'));
const ChallengeGrid = dynamic(() => import('@/components/Ui-components/CommonSections/ChallengeGrid'));
const KeyServices = dynamic(() => import('@/components/Ui-components/CommonSections/KeyServices'));
const ReadinessChecklist = dynamic(() => import('@/components/Ui-components/CommonSections/ReadinessChecklist'));
const CTA = dynamic(() => import('@/components/Ui-components/CommonSections/CTA'));
const IndustrySolutions = dynamic(() => import('@/components/Ui-components/CommonSections/IndustrySolutions'));
const LatestPortfolio = dynamic(() => import('@/components/Ui-components/LatestPortfolio'));
const Testimonials = dynamic(() => import('@/components/Ui-components/Testimonials'));
const DevProcess = dynamic(() => import('@/components/Ui-components/CommonSections/DevProcess'));
const CostBreakdown = dynamic(() => import('@/components/Ui-components/CommonSections/CostBreakdown'));
const HomeBusiness = dynamic(() => import('@/components/Ui-components/home/home-business'));
const ChatbotIntegrations = dynamic(() => import('@/components/Ui-components/chatbot-integrations'));
const CardTechnology = dynamic(() => import('@/components/Ui-components/card-technology'));
const CoreServices = dynamic(() => import('@/components/Ui-components/About-us/coreServices'));
const WhatSetsBest = dynamic(() => import('@/components/Ui-components/CommonSections/WhatSetsBest'));
const Awards = dynamic(() => import('@/components/Ui-components/CommonSections/Awards'));
const HomeServices = dynamic(() => import('@/components/Ui-components/home/home-services'));
const FAQ = dynamic(() => import('@/components/Ui-components/CommonSections/FAQ'));
const LatestInsights = dynamic(() => import('@/components/Ui-components/latest-insights'));

const heroBackground = cdnImage('gen-ai-integrations/generative-ai-integration-services.webp');

const awardsSx = {background: colors.background.default};
const awardsHeadingSx = {color: colors.text.primary};

const GenerativeAIIntegration = (props: any) => {
    const {openForm} = props;

    return (
        <Box className="generative-ai-integration-services-main-parent">
            <PageHead meta={GENERATIVE_AI_INTEGRATION_META_DATA} url="/generative-ai-integration-services" />

            <HeroVideoBanner
                heading="Generative AI Integration Services"
                subheading="We integrate generative AI into your existing applications, enterprise data, and operational workflows to automate complex work, improve decision-making, strengthen employee and customer experiences, and deliver measurable value through secure, scalable, production-ready implementation at scale."
                backgroundImage={heroBackground}
                primaryButtonText="Schedule Free Consultation"
                onPrimaryClick={() => globalThis.open(meetingLink, '_blank', 'noopener,noreferrer')}
                secondaryButtonText="Explore Our Portfolio"
                onSecondaryClick={() => globalThis.open('/portfolio', '_blank', 'noopener,noreferrer')}
            />

            <Box className="home-banner-bottom helper_section_parent">
                <BannerBottom bannerData={genAiBannerBottom} partnerContent="AI Ecosystems We Work With" />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ChallengeGrid
                    heading={genAiChallengesData.heading}
                    subheading={genAiChallengesData.subheading}
                    cards={genAiChallengesData.cards}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <KeyServices
                    heading="Generative AI Integration Services Built Around Business Operations"
                    subheading="We provide strategy, architecture, implementation, deployment, and continuous optimization under one delivery model. Each solution is designed around your existing systems, data controls, business processes, and measurable success criteria."
                    imageSrc={genAiServicesImage}
                    services={genAiKeyServicesData}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <ReadinessChecklist
                    heading={genAiReadinessData.heading}
                    subheading={genAiReadinessData.subheading}
                    items={genAiReadinessData.items}
                    buttonText={genAiReadinessData.buttonText}
                    onButtonClick={() => globalThis.open('/contact-us', '_blank', 'noopener,noreferrer')}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <IndustrySolutions
                    heading={genAiIndustrySolutionsData.heading}
                    subheading={genAiIndustrySolutionsData.subheading}
                    cards={genAiIndustrySolutionsData.cards}
                    variant="compact"
                />
            </Box>
            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <CTA
                    heading={genAiCTA1Data.heading}
                    subheading={genAiCTA1Data.subheading}
                    buttonText={genAiCTA1Data.buttonText}
                    imageSrc={genAiScaleCtaImage}
                    imageAlt={genAiCTA1Data.heading}
                    openForm={() => globalThis.open('/contact-us', '_blank', 'noopener,noreferrer')}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <FeatureGrid
                    type="featureGrid"
                    layout="steps"
                    heading="Architecture Required for Production-Grade Generative AI Integration"
                    intro="A production AI capability is an enterprise architecture decision, not an API connection. We design each layer to support security, performance, maintainability, business context, and controlled access to data and operational systems."
                    image={genAiArchitectureImage}
                    cards={genAiArchitectureData.cards}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <LatestPortfolio
                    heading="Generative AI Case Studies With Measurable Outcomes"
                    contentData={aiPortfolio}
                    showFilters={false}
                    initialCount={6}
                    openForm={openForm}
                    variant="homepage"
                />
            </Box>

            <Box>
                <Testimonials variant="blockchain" heading="Client Testimonials" clientData={aiCarsoulData} />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <WhatSetsBest
                    heading={genAiWhatSetsBestData.heading}
                    body={genAiWhatSetsBestData.body}
                    features={genAiWhatSetsBestData.features}
                    deliverableEyebrow={genAiWhatSetsBestData.deliverableEyebrow}
                    deliverableHeading={genAiWhatSetsBestData.deliverableHeading}
                    deliverables={genAiWhatSetsBestData.deliverables}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <HomeBusiness
                    heading="How Do We Ensure Enterprise-Grade Security, Compliance & Governance?"
                    subheading="Security and governance are designed into the architecture rather than added after deployment. We tailor controls to your data classification, user roles, operating environment, and applicable regulatory obligations."
                    contentData={genAiEnterpriseControlsData}
                />
            </Box>

            <Box className="top-space mobile-top-padding bg-blue-background">
                <ChatbotIntegrations
                    heading="Compliance Frameworks We Design For"
                    subheading="Compliance depends on the organization, data, geography, and use case. We build technical controls that support client programs aligned with the following frameworks."
                    platforms={genAiComplianceFrameworks}
                    platformsPerRow={4}
                    platformIconSize={72}
                    contentData={[]}
                />
            </Box>

            <Grid item xs={12} className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding generative-tech-wrapper">
                <CardTechnology
                    heading="Our Enterprise Generative AI Technology Ecosystem"
                    subheading="We use a model-agnostic approach and select technologies according to the required use case, data controls, integration environment, performance, and total operating cost."
                    data={genAiTechEcosystemData}
                    iconsPerRow={5}
                    cardPaddingX={16}
                    iconSize={40}
                />
            </Grid>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <CoreServices
                    heading="What Business Outcomes Can Enterprise Generative AI Integration Deliver?"
                    subheading="The value of generative AI extends beyond automation. By integrating AI with enterprise applications, business data, and operational workflows, organizations can improve decision-making, increase workforce productivity, accelerate service delivery, reduce operational costs, and establish a scalable foundation for enterprise-wide AI adoption."
                    services={genAiBusinessValueData}
                    iconVariant="navy"
                    linkCard
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <DevProcess
                    heading={genAiDevProcessData.heading}
                    subheading={genAiDevProcessData.subheading}
                    steps={genAiDevProcessData.steps}
                    chevronDown={genAiDevProcessData.chevronDown}
                    chevronRight={genAiDevProcessData.chevronRight}
                    checkIcon={genAiDevProcessData.checkIcon}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding bg-blue-background">
                <Awards
                    heading="Awards & Accolades"
                    awardsList={aiAwardsList}
                    sliding
                    isLightTheme
                    sx={awardsSx}
                    headingSx={awardsHeadingSx}
                />
            </Box>

            <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <CostBreakdown
                    heading="What Determines the Cost of Enterprise Generative AI Integration?"
                    subheading="The cost of enterprise generative AI integration depends on the complexity of your business environment rather than the AI model alone. The number of enterprise systems, data quality, security requirements, workflow complexity, deployment architecture, and expected scale all influence the implementation effort. While focused AI integrations typically start around $25,000–$50,000, enterprise-wide deployments with multiple systems, RAG, AI agents, and governance frameworks commonly range from $100,000 to $500,000+."
                    tableHeaders={genAiCostBreakdownData.tableHeaders}
                    tableRows={genAiCostBreakdownData.tableRows}
                    columns={2}
                />
            </Box>

            <Box className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-blue-background">
                <HomeServices
                    heading="How We Optimize Generative AI for Long-Term Enterprise Performance?"
                    subheading="Production AI is not a static asset. Performance changes as users, data, prompts, models, and business requirements evolve. We therefore treat deployment as the start of a managed improvement cycle, with quality, cost, risk, and adoption reviewed over time."
                    contentData={genAiOptimizationData}
                    iconSize={48}
                    blueHover
                />
            </Box>

            <Box className="top-space mobile-top-padding">
                <CTA
                    heading={genAiCTA2Data.heading}
                    subheading={genAiCTA2Data.subheading}
                    buttonText={genAiCTA2Data.buttonText}
                    imageSrc={genAiReadyCtaImage}
                    imageAlt={genAiCTA2Data.heading}
                    openForm={() => globalThis.open('/contact-us', '_blank', 'noopener,noreferrer')}
                />
            </Box>

            <Grid item xs={12} className="top-space mobile-top-padding">
                <MUIContainer>
                    <FAQ heading="Frequently Asked Questions" faqData={genAiFAQData} />
                </MUIContainer>
            </Grid>

            <Grid item xs={12} className="latest-insights top-space mobile-top-padding">
                <LatestInsights id="21" />
            </Grid>

            <script
                id="generative-ai-integration-services"
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: genAiFAQData[0].questions.map((item) => ({
                            '@type': 'Question',
                            name: item.q,
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: item.a.replace(/<[^>]*>/g, '')
                            }
                        }))
                    })
                }}
            />

            <AggregateScript
                name="Generative AI Integration"
                image={`${CDN_URL}?url=%2Fimages%2Fgenerative-ai-integration%2FGenerative-ai-integration-services.webp&w=750&q=75`}
                description="Integrate generative AI into enterprise applications, data, and workflows with secure RAG, LLMs, AI agents, governance, deployment, and ongoing support."
                offers={{
                    '@type': 'AggregateOffer',
                    url: `${SITE_URL}/generative-ai-integration-services`,
                    priceCurrency: 'USD',
                    lowPrice: '40000',
                    highPrice: '120000',
                    offerCount: '5'
                }}
                aggregateRating={{
                    '@type': 'AggregateRating',
                    ratingValue: '4.7',
                    bestRating: '5',
                    worstRating: '2',
                    ratingCount: '399'
                }}
            />
        </Box>
    );
};

export default GenerativeAIIntegration;
GenerativeAIIntegration.Layout = 'Main';
